import React from 'react'
import Notification from 'rc-notification'

import IconSettings from '@salesforce/design-system-react/components/icon-settings'
import Toast from '@salesforce/design-system-react/components/toast'
import ToastContainer from '@salesforce/design-system-react/components/toast/container'
import classnames from 'classnames'

import styles from './index.less'

let defaultDuration = 3000
let defaultTop: number
let messageInstance: any
let key = 1
let prefixCls = 'global-notice-box'
let transitionName = 'move-up'
let getContainer: () => HTMLElement
let maxCount: number = 1
let rtl = false

function getMessageInstance(callback: (i: any) => void) {
  if (messageInstance) {
    callback(messageInstance)
    return
  }
  Notification.newInstance(
    {
      prefixCls,
      transitionName,
      style: { top: defaultTop }, // 覆盖原来的样式
      getContainer,
      maxCount,
    },
    (instance: any) => {
      if (messageInstance) {
        callback(messageInstance)
        return
      }
      messageInstance = instance
      callback(instance)
    },
  )
}

type NoticeType = 'info' | 'success' | 'error' | 'warning' | 'loading'

export interface ThenableArgument {
  (val: any): void
}

export interface MessageType {
  (): void
  then: (fill: ThenableArgument, reject?: ThenableArgument) => Promise<void>
  promise: Promise<void>
}

export interface ArgsProps {
  content: React.ReactNode
  duration: number | null
  type: NoticeType
  onClose?: () => void
  icon?: React.ReactNode
  key?: string | number
  rtl?: boolean
  disableClose?: boolean
  link?: string
  details?: string
  onClickHeadingLink?: () => void
  hideClose?: boolean
}

interface ICloseNoticeFn {
  (instance: any, key: ArgsProps['key']): void
  flag?: boolean
}

function notice(args: ArgsProps): MessageType {
  const duration = args.duration !== undefined ? args.duration : defaultDuration

  const target = args.key || (key += 1)

  const closePromise = new Promise((resolve) => {
    const callback = () => {
      if (typeof args.onClose === 'function') {
        args.onClose()
      }
      return resolve(true)
    }

    const closeNotice: ICloseNoticeFn = (instance: any, noticeKey: ArgsProps['key']) => {
      instance.removeNotice(noticeKey)
      resolve(true)
    }

    getMessageInstance((instance) => {
      instance.notice({
        key: target,
        duration,
        style: {},
        content: (
          <IconSettings iconPath="/assets/icons">
            <ToastContainer
              className={classnames({
                'ant-message-rtl': args.rtl,
                'hide-toast-close': args.hideClose,
              })}
            >
              <Toast
                duration={duration!}
                labels={{
                  heading: args?.content,
                  headingLink: args?.link,
                  details: args?.details,
                }}
                variant={args.type as Exclude<NoticeType, 'loading'>}
                onClickHeadingLink={args.onClickHeadingLink}
                onRequestClose={() => {
                  closeNotice(instance, target)
                }}
                className={args.disableClose ? styles['notice-toast'] : ''}
              />
            </ToastContainer>
          </IconSettings>
        ),
        onClose: callback,
      })
    })
  })

  const result: any = () => {
    if (messageInstance) {
      messageInstance.removeNotice(target)
    }
  }

  result.then = (filled: ThenableArgument, rejected: ThenableArgument) =>
    closePromise.then(filled, rejected)

  result.promise = closePromise
  return result
}

type ConfigContent = React.ReactNode | string
type ConfigDuration = number | (() => void)
type JointContent = ConfigContent | ArgsProps
export type ConfigOnClose = () => void

function isArgsProps(content: JointContent): content is ArgsProps {
  return (
    Object.prototype.toString.call(content) === '[object Object]' &&
    !!(content as ArgsProps).content
  )
}

export interface ConfigOptions {
  top?: number
  duration?: number
  prefixCls?: string
  getContainer?: () => HTMLElement
  transitionName?: string
  maxCount?: number
  rtl?: boolean
}

const api: any = {
  open: notice,
  config(options: ConfigOptions) {
    if (options.top !== undefined) {
      defaultTop = options.top
      messageInstance = null // delete messageInstance for new defaultTop
    }
    if (options.duration !== undefined) {
      defaultDuration = options.duration
    }
    if (options.prefixCls !== undefined) {
      prefixCls = options.prefixCls
    }
    if (options.getContainer !== undefined) {
      getContainer = options.getContainer
    }
    if (options.transitionName !== undefined) {
      transitionName = options.transitionName
      messageInstance = null // delete messageInstance for new transitionName
    }
    if (options.maxCount !== undefined) {
      maxCount = options.maxCount
      messageInstance = null
    }
    if (options.rtl !== undefined) {
      rtl = options.rtl
    }
  },
  destroy() {
    if (messageInstance) {
      messageInstance.destroy()
      messageInstance = null
    }
  },
}

;['success', 'info', 'warning', 'error', 'loading'].forEach((type) => {
  api[type] = (content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose) => {
    if (isArgsProps(content)) {
      return api.open({ ...content, type })
    }

    if (typeof duration === 'function') {
      return api.open({ content, undefined, type, duration })
    }

    return api.open({ content, duration, type, onClose })
  }
})

api.warn = api.warning

export interface MessageApi {
  info(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  success(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  error(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  warn(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  warning(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  loading(content: JointContent, duration?: ConfigDuration, onClose?: ConfigOnClose): MessageType
  open(args: ArgsProps): MessageType
  config(options: ConfigOptions): void
  destroy(): void
}
export default api as MessageApi
